// Page Abonnement (lot B10) : l'abonnement comme une ligne du registre, pas comme une page de prix. Ce que ton
// compte ouvre aujourd'hui, ce que l'abonnement paie en plus, ce qui restera toujours gratuit, et l'usage IA du
// mois quand il y a un plafond. Le prix ne s'écrit que si Stripe est vraiment configuré (`offre.stripe_pret`) :
// sinon la page dit que l'offre n'est pas ouverte, et n'invente pas un tarif.
//
// Le paiement part chez Stripe (Checkout) ; au retour sur /abonnement?session_id=…, window.abonnementRetour()
// relit la session (stripe_sync) et pose e.retour : vérification → payé / en attente ; ?annule → annulé. Ces
// quatre états sont des bandeaux en ligne, jamais des fenêtres.
import { html, attr } from '../../core/html.js';
import { ico } from '../icones.js';
import { ligne, alerte } from './compte.js';

// Ce que l'abonnement ouvre, et ce qui reste gratuit : icône du sprite Phosphor, titre, une phrase.
var INCLUS = [
    ['stack', 'Flashcards générées', 'Cartes de découverte ou ciblées sur tes points faibles, à partir de la fiche EDN.'],
    ['graduation-cap', 'Mini-cours ciblés', 'Un cours écrit pour ce que tu rates, QCM et cartes non acquises analysés.'],
    ['brain', 'Correction de l\'active recall', 'Tu récites, l\'IA compare à la fiche et repère ce qui manque.'],
    ['baby', 'QCM pédiatrie', 'Des QCM rédigés sur les particularités de l\'enfant pour les renforcements.'],
    ['tree-structure', 'QCM répartis par sous-partie', 'Les QCM edn.fr rattachés à chaque partie de tes items scindés.'],
];
var GRATUIT = [
    ['calendar-check', 'Planning J adaptatif', 'Ordonnancement, retards, partiels, statistiques.'],
    ['file-text', 'Fiches et podcasts', 'Tout ce que l\'administrateur a généré, en lecture et en écoute.'],
    ['question', 'QCM edn.fr', 'Sessions, corrections partagées, historique et notions faibles.'],
    ['stack', 'Flashcards existantes', 'Révision espacée des decks déjà créés, sans limite.'],
];
var OFFRE_DEFAUT = { mensuel_cents: 0, annuel_cents: 0, annuel: false, stripe_pret: false };
// Les quatre retours de Stripe : un mot, une icône, un aimant. Jamais la couleur seule.
var RETOURS = {
    verification: { icone: 'circle-notch', classe: 'abo-retour--attente', tourne: true, texte: 'Vérification du paiement auprès de Stripe…' },
    ok: { icone: 'check-circle', classe: 'abo-retour--ok', texte: 'Merci. Ton abonnement est actif : les fonctions IA sont ouvertes dès maintenant.' },
    attente: { icone: 'clock', classe: 'abo-retour--attente', texte: 'Paiement en cours de confirmation. Si l\'abonnement n\'apparaît pas d\'ici quelques minutes, recharge cette page.' },
    annule: { icone: 'info', classe: '', texte: 'Paiement annulé : rien n\'a été débité.' },
};

// Les états d'abonnement de Stripe, en français. Un état inconnu se montre tel quel plutôt que d'être caché :
// mieux vaut un mot de Stripe qu'un trou, et c'est la seule page où il puisse se lire.
var STATUTS = {
    active: 'actif', trialing: 'période d\'essai', past_due: 'paiement en retard', unpaid: 'impayé',
    canceled: 'résilié', incomplete: 'paiement incomplet', incomplete_expired: 'paiement abandonné', paused: 'en pause',
};
function statutFr(v) { var s = String(v || ''); return STATUTS[s] || s; }

function etat() { return window.abonnementEtat; }
function dateFr(v) { return v ? window.formatDateFr(String(v).slice(0, 10)) : ''; }

export function squeletteAbonnement() {
    return html`<div class="squelettes" aria-hidden="true">
        <div class="jour-entete"><div class="jour-entete-texte"><div class="squelette squelette-titre" style="width:200px"></div><div class="squelette squelette-ligne" style="width:380px"></div></div></div>
        <div class="squelette squelette-bloc" style="margin-top:24px;height:180px"></div>
        <div class="squelette squelette-ligne" style="width:64%"></div><div class="squelette squelette-ligne" style="width:58%"></div><div class="squelette squelette-ligne" style="width:70%"></div>
    </div>`;
}

// --- la carte d'état : l'aimant, les faits connus en lignes de registre, l'action à droite
// L'erreur d'action (« Stripe n'a pas répondu ») se pose ici, sous la phrase et juste au-dessus du bouton qui
// vient d'échouer, et non en tête de page : seul le retour de Stripe passe au-dessus de tout (FIRST VIEWPORT).
function carteEtat(e, info, offre, admin, actif) {
    var chargement = !!e.chargement;
    var aimant, titre, phrase, lignes = [], action = '';

    if (admin) {
        aimant = html`<span class="puce puce-accent">Administrateur</span>`;
        titre = 'Toutes les fonctions sont incluses';
        phrase = 'Aucun plafond sur ton compte. Les abonnés et leur consommation du mois sont listés plus bas.';
        lignes.push(ligne('Stripe', offre.stripe_pret
            ? html`<span class="reg-valeur"><span class="puce puce-ok">Configuré</span><span class="reg-texte">L'offre est ouverte aux utilisateurs.</span></span>`
            : html`<span class="reg-valeur"><span class="puce puce-warn">Non configuré</span><span class="reg-texte">Renseigne <code class="num">$stripe_secret_key</code>, <code class="num">$stripe_price_id</code> et <code class="num">$stripe_webhook_secret</code> dans <code class="num">config.local.php</code> pour ouvrir l'offre.</span></span>`));
    } else if (actif) {
        var resilie = !!(info && info.annule_fin_periode);
        aimant = resilie
            ? html`<span class="puce puce-warn">Résilié, encore ouvert</span>`
            : html`<span class="puce puce-ok">Abonnement actif</span>`;
        titre = 'Les fonctions IA sont ouvertes';
        phrase = resilie
            ? 'La résiliation est enregistrée : les fonctions IA restent ouvertes jusqu\'à la fin de la période déjà payée.'
            : 'Ton abonnement se renouvelle tout seul. Tu peux le résilier à tout moment : l\'accès reste ouvert jusqu\'à la fin de la période payée.';
        if (info && info.plan) lignes.push(ligne('Formule', html`<span class="reg-fort">${info.plan}</span>`));
        lignes.push(ligne(resilie ? 'Ouvert jusqu\'au' : 'Prochain renouvellement', html`<span class="reg-fort num">${dateFr(info && info.fin) || '—'}</span>`));
        if (info && info.plafond_cents) {
            var pct = Math.min(100, Math.round(info.usage_cents / info.plafond_cents * 100));
            lignes.push(ligne('Usage IA ce mois-ci', html`<div class="abo-usage">
                <div class="abo-usage-chiffres"><span class="num reg-fort">${window.euros(info.usage_cents)}</span><span class="num reg-note">sur ${window.euros(info.plafond_cents)}</span></div>
                <span class="jauge-piste abo-jauge" role="img" aria-label="Usage IA du mois : ${pct} % du plafond"><span class="jauge-val" style="--p:${pct / 100}"></span></span>
                <p class="reg-texte">Chaque génération a un coût d'API réel ; le compteur repart de zéro le 1er du mois. Au plafond, les fonctions IA attendent le mois suivant — les QCM, fiches et podcasts restent ouverts.</p>
            </div>`));
        }
        action = html`<button type="button" class="btn btn-secondaire" data-action="stripePortal"${chargement ? html` aria-busy="true" disabled` : ''}>${ico('gear-six', { taille: 18 })}Gérer / résilier</button>`;
    } else {
        aimant = html`<span class="puce puce-sans-point">Sans abonnement</span>`;
        titre = 'Les fonctions IA sont fermées';
        phrase = 'Tu as le planning, le catalogue, les fiches, les podcasts, les QCM et leurs corrections. L\'abonnement ajoute les cinq fonctions IA personnelles listées plus bas.';
        if (info && info.statut && info.statut !== 'none') {
            lignes.push(ligne('Dernier état connu', html`<span class="reg-valeur"><span class="reg-fort">${statutFr(info.statut)}</span>${info.fin ? html`<span class="reg-note num">fin de période le ${dateFr(info.fin)}</span>` : ''}<button type="button" class="lien-bouton" data-action="stripePortal">Voir les factures</button></span>`));
        }
        if (offre.stripe_pret) {
            lignes.push(ligne('Prix', html`<span class="reg-valeur"><span class="reg-fort num">${window.euros(offre.mensuel_cents)} / mois</span>${offre.annuel ? html`<span class="reg-note num">ou ${window.euros(offre.annuel_cents)} / an, deux mois offerts</span>` : ''}</span>`));
            action = html`<div class="abo-actions">
                <button type="button" class="btn btn-primaire" data-action="stripeCheckout" data-args="${attr(['mensuel'])}"${chargement ? html` aria-busy="true" disabled` : ''}>${ico('credit-card', { taille: 18 })}S'abonner · ${window.euros(offre.mensuel_cents)} / mois</button>
                ${offre.annuel ? html`<button type="button" class="btn btn-fantome" data-action="stripeCheckout" data-args="${attr(['annuel'])}"${chargement ? html` aria-busy="true" disabled` : ''}>${window.euros(offre.annuel_cents)} / an</button>` : ''}
            </div>`;
        } else {
            lignes.push(ligne('Prix', html`<span class="reg-texte">L'offre n'est pas encore ouverte : le paiement et son tarif s'afficheront ici quand l'administrateur aura configuré Stripe.</span>`));
        }
    }

    return html`<section class="carte carte-corps abo-etat" aria-labelledby="abo-etat-titre">
        <div class="abo-etat-tete">
            <div class="abo-etat-texte">
                ${aimant}
                <h2 id="abo-etat-titre">${titre}</h2>
                <p class="abo-etat-phrase">${phrase}</p>
                ${e.erreur ? alerte(e.erreur) : ''}
            </div>
            ${action}
        </div>
        ${lignes.length ? html`<dl class="reg">${lignes}</dl>` : ''}
    </section>`;
}

function listeAvantages(id, titre, phrase, items) {
    return html`<section class="abo-section" aria-labelledby="${id}">
        <h2 id="${id}">${titre}</h2>
        <p class="abo-section-phrase">${phrase}</p>
        <ul class="reg reg-liste">${items.map(function(x) {
            return html`<li class="reg-ligne reg-ligne--item"><span class="reg-ico">${ico(x[0], { taille: 20 })}</span><span class="reg-item-corps"><span class="reg-fort">${x[1]}</span><span class="reg-texte">${x[2]}</span></span></li>`;
        })}</ul>
    </section>`;
}

// --- administration : les comptes et ce qu'ils ont consommé ce mois-ci (jamais montré à un utilisateur)
function tableauComptes(e) {
    if (!e.comptes && !e.comptesDemande) {
        e.comptesDemande = true;
        window.syncWithServer('abonnes_liste', {}, function(d) { if (d && d.success) e.comptes = d.comptes || []; else e.comptes = []; window.renderContent(); });
    }
    var c = e.comptes;
    return html`<section class="abo-section" aria-labelledby="abo-comptes-titre">
        <h2 id="abo-comptes-titre">Comptes et consommation du mois</h2>
        <p class="abo-section-phrase">Visible de l'administrateur seulement.</p>
        ${!c ? html`<div class="squelettes" aria-hidden="true"><div class="squelette squelette-ligne" style="width:80%"></div><div class="squelette squelette-ligne" style="width:72%"></div></div>`
            : (!c.length ? html`<p class="abo-vide">Aucun compte.</p>`
            : html`<div class="abo-table-cadre"><table class="abo-table">
                <thead><tr><th scope="col">Compte</th><th scope="col">Rôle</th><th scope="col">Abonnement</th><th scope="col" class="abo-td-num">Appels IA</th><th scope="col" class="abo-td-num">Coût API</th></tr></thead>
                <tbody>${c.map(function(x) {
                    return html`<tr>
                        <th scope="row">${x.email}</th>
                        <td>${x.role}</td>
                        <td>${x.status && x.status !== 'none' ? html`${statutFr(x.status)}${x.plan ? ' · ' + x.plan : ''}${x.current_period_end ? html` <span class="num reg-note">→ ${dateFr(x.current_period_end)}</span>` : ''}` : '—'}</td>
                        <td class="abo-td-num num">${x.appels_mois || 0}</td>
                        <td class="abo-td-num num">${window.euros(x.cout_mois || 0)}</td>
                    </tr>`;
                })}</tbody>
            </table></div>`)}
    </section>`;
}

export function renderAbonnement() {
    var e = etat(), info = e.info, admin = window.estAdmin();
    // Une seule demande : un serveur qui répond sans « abonnement » ne doit pas relancer la lecture à chaque rendu.
    if (!info && !e.chargement && !e.erreur && !e.demande) { e.demande = true; window.chargerAbonnement(); }
    var offre = (info && info.offre) || OFFRE_DEFAUT;
    var actif = !!(info && info.actif);
    var r = e.retour ? RETOURS[e.retour] : null;

    return html`<div class="abo fade-in">
        ${r ? html`<p class="abo-retour ${r.classe}" role="status">${ico(r.icone, { taille: 18, classe: r.tourne ? 'abo-tourne' : '' })}<span>${r.texte}</span></p>` : ''}
        <header class="jour-entete">
            <div class="jour-entete-texte">
                <h1 class="jour-date">Abonnement</h1>
                <p class="abo-resume">Le planning, les fiches, les podcasts et les QCM sont ouverts à tout compte. L'abonnement paie ce que l'IA écrit pour toi seul.</p>
            </div>
        </header>

        ${carteEtat(e, info, offre, admin, actif)}
        ${listeAvantages('abo-inclus-titre', 'Ce que l\'abonnement ouvre', 'Cinq fonctions, toutes écrites à la demande, à partir de tes propres résultats.', INCLUS)}
        ${listeAvantages('abo-gratuit-titre', 'Toujours gratuit, pour tout compte', 'Sans abonnement, rien de tout cela ne se ferme.', GRATUIT)}
        ${admin ? tableauComptes(e) : ''}

        ${!admin && !actif && offre.stripe_pret ? html`<footer class="abo-pied"><p class="abo-note">Paiement sécurisé par Stripe : le site ne voit jamais ton numéro de carte. Résiliable à tout moment depuis cette page, l'accès restant ouvert jusqu'à la fin de la période payée. Prix TTC. En payant, tu acceptes les <a href="${window.pageUrl('cgv')}">conditions d'utilisation et de vente</a> et tu renonces au délai de rétractation pour un service numérique fourni immédiatement.</p></footer>` : ''}
    </div>`;
}

window.renderAbonnement = renderAbonnement;
window.squeletteAbonnement = squeletteAbonnement;
